import { z } from "zod";
import type { ModuleFeature } from "../features";
import type { Permission } from "../permissions";
import { pageQuery, uuid } from "./common";

/** Уведомления (ТЗ 16 §16.1–16.2): центр уведомлений, email, Telegram. */
export const NOTIFICATION_TYPES = ["stock.low", "shift.closed", "refund.created", "report.daily", "order.qr", "table.call", "supplier.due", "support.reply", "support.access", "fraud.alert", "sms.quota", "stock.expiring", "stock.request", "goal.achieved", "achievement.earned", "waiter.table", "purchase.request", "purchase.decision", "platform.message", "platform.update"] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
export type NotificationChannel = "app" | "email" | "telegram";

/**
 * Каталог (ТЗ 16 §16.1): кто получает (право в филиале) и по каким каналам. Пользователь видит в настройках и получает
 * только типы, на которые у его ролей есть право (и функция `feature` есть в тарифе).
 */
export const NOTIFICATION_CATALOG: Record<NotificationType, { permission: Permission | null; channels: NotificationChannel[]; feature?: ModuleFeature }> = {
  "stock.low": { permission: "stock.view", channels: ["app", "telegram"], feature: "core.inventory_basic" },
  "shift.closed": { permission: "analytics.view", channels: ["app", "telegram", "email"] },
  "refund.created": { permission: "analytics.view", channels: ["app", "telegram"] },
  "report.daily": { permission: "analytics.view", channels: ["app", "telegram", "email"], feature: "core.analytics_basic" },
  "order.qr": { permission: "pos.view", channels: ["app", "telegram"], feature: "addon.qr_ordering" },
  "table.call": { permission: "pos.view", channels: ["app", "telegram"] },
  "supplier.due": { permission: "supplier_payments.view", channels: ["app", "telegram", "email"], feature: "addon.procurement" },
  "support.reply": { permission: "support.view", channels: ["app", "telegram", "email"] },
  /** Сотрудник Coca просит войти в кабинет — владельцу и управляющим (подтвердить или отклонить) */
  "support.access": { permission: "support.grant_access", channels: ["app", "telegram", "email"] },
  "fraud.alert": { permission: "fraud.view", channels: ["app", "telegram", "email"], feature: "addon.fraud" },
  "sms.quota": { permission: "subscription.view", channels: ["app", "email"], feature: "addon.sms" },
  "stock.expiring": { permission: "stock.view", channels: ["app", "telegram"], feature: "addon.inventory_pro" },
  "stock.request": { permission: "stock_transfers.create", channels: ["app", "telegram"], feature: "addon.inventory_pro" },
  /** Общая цель выполнена — тем, кто видит цели; личная — самому сотруднику (userIds) */
  "goal.achieved": { permission: "goals.view", channels: ["app", "telegram"], feature: "core.staff" },
  /** Личное: новое достижение сотрудника (permission null — каждому пользователю, только через userIds) */
  "achievement.earned": { permission: null, channels: ["app", "telegram"], feature: "core.staff" },
  /** Личное: официанту назначили или передали стол (распределение по зонам) */
  "waiter.table": { permission: null, channels: ["app", "telegram"], feature: "core.tables" },
  /** Новая заявка на закупку — тем, кто её одобряет */
  "purchase.request": { permission: "purchase_requests.manage", channels: ["app", "telegram"], feature: "core.inventory_basic" },
  /** Личное: автору — заявку на закупку одобрили, отклонили или купили */
  "purchase.decision": { permission: null, channels: ["app", "telegram"], feature: "core.inventory_basic" },
  /** Личное: сообщение от Coca (рассылка Platform Admin) — адресаты выбраны платформой, без проверки тарифа и статуса подписки */
  "platform.message": { permission: null, channels: ["app", "telegram", "email"] },
  /** Личное: новая функция в Coca (`WHATS_NEW`) — адресатов выбирает `WhatsNewService` (тариф, право, работающая подписка) */
  "platform.update": { permission: null, channels: ["app", "telegram"] },
};

const types = z.array(z.enum(NOTIFICATION_TYPES)).max(NOTIFICATION_TYPES.length);
/** Настройки пользователя: каналы, отключённые типы и отключённые каналы по типам. */
export const notificationPrefsSchema = z.strictObject({
  email: z.boolean(),
  telegram: z.boolean(),
  /** Дневной отчёт по филиалам (владельцу/управляющему) */
  dailyReport: z.boolean(),
  /** Тип не приходит совсем (ни в приложение, ни в каналы) */
  muted: types,
  /** Тип приходит в приложение, но не в Telegram / не на почту */
  telegramMuted: types,
  emailMuted: types,
}).partial();
export type NotificationPrefs = { email: boolean; telegram: boolean; dailyReport: boolean; muted: NotificationType[]; telegramMuted: NotificationType[]; emailMuted: NotificationType[] };
export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = { email: true, telegram: true, dailyReport: true, muted: [], telegramMuted: [], emailMuted: [] };

const flag = z.enum(["true", "false"]).transform((v) => v === "true");
export const notificationsQuery = pageQuery.extend({ unread: flag.optional() });
export const notificationsReadSchema = z
  .strictObject({ ids: z.array(uuid).min(1).max(200).optional(), all: z.literal(true).optional() })
  .refine((v) => !!v.ids !== !!v.all, { error: "Укажите ids или all", path: ["ids"] });
